import { useContext, useEffect, useState } from "react";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { Box, Dialog, Divider, Menu, MenuItem, Stack, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { FarmVaultIcon } from "components/Icons/Icons";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useSnackbar } from "notistack";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import FarmCountyCard from "./components/FarmCountyCard";
import FarmCountyDataTable from "./components/FarmCountyDataTable";
import ProducerFarmCopyAipPassDialog from "./components/Import/ProducerFarmCopyAipPassDialog";
import ProducerFarmCopyDialog from "./components/Import/ProducerFarmCopyDialog";
import { getUniqueCounties } from "./helpers";

export default function VaultPotentialWorkspace() {
  const { globalState } = useContext(AppContext);

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();

  const CARD_GRID_KEY = "farm_potential";
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const { addAttachments } = useChatAssistantAttachments();
  const [isCopyYearDialogOpen, setIsCopyYearDialogOpen] = useState(false);
  const handleCopyYearDialogClose = () => {
    setIsCopyYearDialogOpen(false);
  };

  const [isCopyAipPassDialogOpen, setIsCopyAipPassDialogOpen] = useState(false);
  const handleCopyAipPassDialogClose = () => {
    setIsCopyAipPassDialogOpen(false);
    loadPotential();
  };

  const { component: confirmDialogComponent } = useDialog();

  const [isLoadingPotential, setIsLoadingPotential] = useState(true);
  const { state: potentialCards, actions: potentialActions } = useCardController();

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadPotential = async () => {
    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        is_active: true,
      };
      const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
      const potentials = getUniqueCounties(farms);
      potentialActions.replace(potentials);

      setIsLoadingPotential(false);
    } catch (err) {
      setIsLoadingPotential(false);
      potentialActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadPotential();
    }
  }, [globalState?.producer_token]);

  useEffect(() => {
    // Add crops as chat client attachments
    addAttachments([
      {
        name: `Farm Potential`,
        content: potentialCards?.length > 0 ? potentialCards : [],
      },
    ]);
  }, [potentialCards]);

  return (
    <>
      <WorkspaceHeader
        title="Farm Potential"
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
      />

      <WorkspacePageHeader
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoadingPotential}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {potentialCards.map((currentCountyPractice, index) => (
            <FarmCountyCard currentCounty={currentCountyPractice} key={index} />
          ))}
          {!potentialCards?.length && (
            <EmptyCard
              text="No farms listed"
              secondaryText="Get started by importing history or adding a farm"
              isLoading={isLoadingPotential}
            />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <FarmCountyDataTable currentCards={potentialCards} isLoading={isLoadingPotential} />
      )}

      <WorkspaceActionBar
        content={
          <>
            <Stack direction="row" spacing={2}>
              {/* {[USER_ROLE_ADMIN, USER_ROLE_AGENT].includes(globalState?.user_role) && */}
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <>
                    <Button variant="contained" {...bindTrigger(popupState)}>
                      <ArrowDropDownOutlined sx={{ mr: 1 }} />
                      Import
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem
                        onClick={() => {
                          setIsCopyYearDialogOpen(true);
                          popupState.close();
                        }}
                      >
                        <ContentCopyOutlined sx={{ mr: 1 }} /> Copy Prior Season ...
                      </MenuItem>
                      <MenuItem disabled>
                        <UploadFileOutlined sx={{ mr: 1 }} /> Upload file ...
                      </MenuItem>

                      <Divider />

                      <MenuItem
                        onClick={() => {
                          setIsCopyAipPassDialogOpen(true);
                          popupState.close();
                        }}
                      >
                        <CloudDownloadOutlined sx={{ mr: 1 }} /> Import from History ...
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </PopupState>
            </Stack>
          </>
        }
      />

      <Dialog open={isCopyYearDialogOpen} fullWidth={isSmall} PaperProps={{ sx: { minHeight: "80vh" } }}>
        <ProducerFarmCopyDialog onClose={handleCopyYearDialogClose} />
      </Dialog>

      <Dialog open={isCopyAipPassDialogOpen} fullWidth={isSmall} PaperProps={{ sx: { minHeight: "80vh" } }}>
        <ProducerFarmCopyAipPassDialog onClose={handleCopyAipPassDialogClose} />
      </Dialog>

      {confirmDialogComponent}
    </>
  );
}
